import { Injectable } from "@angular/core";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { OrderModel } from "src/app/models/order.model";
import { ProductModel } from "src/app/models/product.model";
import { ProfileModel } from "src/app/models/profile.model";
import * as XLSX from "xlsx-js-style";
import { DateService } from "../date/date.service";
import { MathService } from "../math/math.service";

const EXCEL_TYPE = {
  xlsx:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  ods:"application/vnd.oasis.opendocument.spreadsheet"
};
const EXCEL_EXTENSION = {
  xlsx:".xlsx",
  ods:".ods"
};

@Injectable({
  providedIn: "root"
})
export class ExcelService {

  priceTranslation = this.translateService.instant("order.Price");
  quantityTranslation = this.translateService.instant("order.Quantity");
  totalTranslation = this.translateService.instant("tournee.Total");
  ordersTranslation = this.translateService.instant("order.Orders");
  unitPriceTranslation = this.translateService.instant("order.Price");
  amountTranslation = this.translateService.instant("order.Amount");
  metricTranslation = this.translateService.instant("productCatalog.Metric");
  sizingTranslation = this.translateService.instant("productCatalog.Sizing");
  datePlaceTranslation = this.translateService.instant("tournee.DatePlace");
  currencyTranslation = this.translateService.instant("productCatalog.Currency");
  clientTranslation = this.translateService.instant("order.Client");
  phoneTranslation = this.translateService.instant("order.PhoneNumber");
  emailTranslation = this.translateService.instant("order.Email");
  deliveryTranslation = this.translateService.instant("tournee.Delivery");
  depositTranslation = this.translateService.instant("productCatalog.Deposit");
  orderIDTranslation = this.translateService.instant("order.ID");
  productTranslation = this.translateService.instant("order.Product");
  messageTranslation = this.translateService.instant("order.Message");
  paymentStatusTranslation = this.translateService.instant("order.PaymentStatus");
  statusTranslation = this.translateService.instant("order.Status");
  saleTranslation = this.translateService.instant("tournee.Sale");
  orderDateTranslation = this.translateService.instant("order.OrderDate");
  dateTranslation = this.translateService.instant("order.Date");
  producerTranslation = this.translateService.instant("account.profiles.Producer");
  headerStyle = {
      font: { bold: true, color: { rgb: "ee940d" }},
      fill: { fgColor: { rgb: "ecebe7" } },
      alignement:{horizontal:'center'}
      // border:{left:{style:'thin',color:"585757"} }
    };
  firstColStyle = {
      font: { bold: true},
      fill: { fgColor: { rgb: "F9F6F0" } },
      alignement:{horizontal:'center'}
      // border:{left:{style:'thin',color:"585757"} }
    };
  borderStyle = {style:'thin',color:"585757"};

  constructor(private translateService: TranslateService, private mathService: MathService, 
    private alertCtrl:AlertController, private dateService: DateService) {
  }
  
  
  async exportAsExcelFile(json: any, excelFileName: string): Promise<void> {
    // console.log(json);
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("export.FormatTitle"),
      message: this.translateService.instant("export.FormatSubtitle"),
      backdropDismiss: true,
      buttons: [
        {
          role: "xlsx",
          text: this.translateService.instant("export.XLSX"),
          handler: () => {
            this.generateFile(json,excelFileName,"xlsx" as XLSX.BookType)
          }
        },
        {
          role: "ods",
          text: this.translateService.instant("export.ODS"),
          handler: () => {
            this.generateFile(json,excelFileName,"ods" as XLSX.BookType)
          }
        }
      ]
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  private generateFile(json: any, excelFileName: string, format:XLSX.BookType) {
    const worksheetByProduct: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.byProduct, {skipHeader:true});
    const worksheetByClient: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.byClient, {skipHeader:true});
    const worksheetSizings: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.productSizings, {skipHeader:true});
    const worksheetConsumers: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.consumers, {skipHeader:true});
    const worksheetProductSummary: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.productSummary, {skipHeader:true});
    const worksheetProducerSummary: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.producerSummary, {skipHeader:true});
    const worksheetByLines: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json.byLines, {skipHeader:true});

    const workbook: XLSX.WorkBook = { 
      Sheets: { 
        [this.translateService.instant("export.ByProduct")]: worksheetByProduct,
        [this.translateService.instant("export.ByClient")]:worksheetByClient, 
        [this.translateService.instant("export.Sizings")]:worksheetSizings,
        [this.translateService.instant("export.ProductSummary")]:worksheetProductSummary,
        [this.translateService.instant("export.ProducerSummary")]:worksheetProducerSummary,
        [this.translateService.instant("export.Clients")]:worksheetConsumers,
        [this.translateService.instant("export.ByLines")]:worksheetByLines,
      },
      SheetNames: [
        this.translateService.instant("export.ByProduct"),
        this.translateService.instant("export.ByClient"),
        this.translateService.instant("export.Sizings"),
        this.translateService.instant("export.ProductSummary"),
        this.translateService.instant("export.ProducerSummary"),
        this.translateService.instant("export.Clients"),
        this.translateService.instant("export.ByLines"),
      ]
    };
    

    workbook.Sheets[this.translateService.instant("export.ByProduct")]["!cols"] = [
      {wch:30},{wch:15} 
    ];
    workbook.Sheets[this.translateService.instant("export.ByClient")]["!cols"] = [
      {wch:30},{wch:40}
    ];  
    workbook.Sheets[this.translateService.instant("export.Clients")]["!cols"] = [
      {wch:30},{wch:30},{wch:50}
    ]; 
    workbook.Sheets[this.translateService.instant("export.ByLines")]["!cols"] = [
      {wch:30},{wch:40},{wch:30},{wch:30}, {wch:15},{wch:15},{wch:15}, {wch:30}
    ]; 
    workbook.Sheets[this.translateService.instant("export.Sizings")]["!cols"] = [
      {wch:30},{wch:40},{wch:20}
    ];
    workbook.Sheets[this.translateService.instant("export.ProductSummary")]["!cols"] = [
      {wch:30},{wch:30},{wch:20} 
    ];
    workbook.Sheets[this.translateService.instant("export.ProducerSummary")]["!cols"] = [
      {wch:30},{wch:30},{wch:20} 
    ];

    const mergesByClient = [];
    for(let i = 0; i <= (json.consumers.length+1); i++){
      mergesByClient.push({s:{r:0,c:(2+i*2)},e:{r:0,c:(3+i*2)}});
    }
    workbook.Sheets[this.translateService.instant("export.Clients")]["!merges"] = mergesByClient;
    
    const mergesByProduct = [];
    for(let j = 0; j <= json.products.length; j++){
      mergesByProduct.push({s:{r:0,c:(1+j*2)},e:{r:0,c:(2+j*2)}});
      mergesByProduct.push({s:{r:1,c:(1+j*2)},e:{r:1,c:(2+j*2)}});
    }
    workbook.Sheets[this.translateService.instant("export.ByProduct")]["!merges"] = mergesByProduct;
    XLSX.writeFile(workbook,excelFileName + EXCEL_EXTENSION[format],{bookType:format})
    //const excelBuffer: any = XLSX.write(workbook, { bookType: format, type: "array" });
    //this.saveAsExcelFile(excelBuffer, excelFileName, format);
  }

  private generateFileGeneric(json: any, excelFileName: string, format:XLSX.BookType) {
    const workbook: XLSX.WorkBook = { Sheets:{}, SheetNames:[]};
    for(const [key,value] of Object.entries(json)){
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(value as [], {skipHeader:true});
      workbook.Sheets["export."+key] = worksheet;
      workbook.SheetNames.push(this.translateService.instant("export."+key));
      workbook.Sheets["export."+key]["!cols"] = [
        {wch:50},{wch:50},{wch:70} 
      ];
    };
    XLSX.writeFile(workbook,excelFileName + EXCEL_EXTENSION[format],{bookType:format})
    //const excelBuffer: any = XLSX.write(workbook, { bookType: format, type: "array" });
    //this.saveAsExcelFile(excelBuffer, excelFileName, format);
  }


  private saveAsExcelFile(buffer: any, fileName: string, format:string): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE[format] });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION[format]);
  }

  public exportOrders(orders: OrderModel[], filename: string) {
    const consumers = {};
    const products = {};
    const byClients:any = {};
    const byProducts:any = {};

    const commandes: any = {byLines:[],
      byClient:[],
      byProduct:[],
      consumers:[],
      products:[],
      productSizings:[], 
      productSummary:[],
      producerSummary:[]};
    const productStats:any = {};//{0:{products:{0:{orders:{}, sizings:{}}}}};
    const productLines:any = {};
    const producerLines:any = {};
    const byProductHeader:any = {
      productName:{t:'s',v:this.productTranslation,s:this.headerStyle},
    };
    const byProductSubHeader:any = {
      productName:{t:'s',v:'',s:this.headerStyle},
    };
    const byClientHeader:any = {
      // [this.orderIDTranslation]:{t:'s',v:this.orderIDTranslation,s:this.headerStyle},
      client:{t:'s',v:this.clientTranslation,s:this.headerStyle},
      datePlace:{t:'s',v:this.datePlaceTranslation,s:this.headerStyle},
      // [this.totalTranslation]:{t:'s',v:this.totalTranslation,s:this.headerStyle}
    };
    const clientHeader:any = {
      client:{t:'s',v:this.clientTranslation,s:this.headerStyle},
      phone:{t:'s',v:this.phoneTranslation,s:this.headerStyle},
      email:{t:'s',v:this.emailTranslation,s:this.headerStyle},
    };
    const productHeader:any = {
      datePlace:{t:'s',v:this.datePlaceTranslation,s:this.headerStyle},
      producer:{t:'s',v:this.producerTranslation,s:this.headerStyle},
      product:{t:'s',v:this.productTranslation,s:this.headerStyle},
      quantity:{t:'s',v:this.quantityTranslation,s:this.headerStyle},
      price:{t:'s',v:this.priceTranslation,s:this.headerStyle},
      total:{t:'s',v:this.totalTranslation,s:this.headerStyle},
    };

    const producerSummaryHeader:any = {
      producer:{t:'s',v:this.producerTranslation,s:this.headerStyle},
      nbr:{t:'s',v:this.ordersTranslation,s:this.headerStyle},
      total:{t:'s',v:this.totalTranslation,s:this.headerStyle},
      deposit:{t:'s',v:this.depositTranslation,s:this.headerStyle},
      transport:{t:'s',v:this.deliveryTranslation,s:this.headerStyle},
    };

    const lineHeader:any = {
      client:{t:'s',v:this.clientTranslation,s:this.headerStyle},
      datePlace:{t:'s',v:this.datePlaceTranslation,s:this.headerStyle},
      producer:{t:'s',v:this.producerTranslation,s:this.headerStyle},
      product:{t:'s',v:this.productTranslation,s:this.headerStyle},
      quantity:{t:'s',v:this.quantityTranslation,s:this.headerStyle},
      price:{t:'s',v:this.priceTranslation,s:this.headerStyle},
      status:{t:'s',v:this.statusTranslation,s:this.headerStyle},
      orderId:{t:'s',v:this.orderIDTranslation,s:this.headerStyle},
    };

    const productSizingsHeader = {
      product:{t:'s',v:this.productTranslation,s:this.headerStyle},
      datePlace:{t:'s',v:this.datePlaceTranslation,s:this.headerStyle},
      sizing:{t:'s',v:this.sizingTranslation,s:this.headerStyle},
      quantity:{t:'s',v:this.quantityTranslation,s:this.headerStyle},
      price:{t:'s',v:this.priceTranslation,s:this.headerStyle}
    };

    for (const order of orders) {
      if (order.status!=='rejected' && order.status!=='cancelled') {
        const datePlaceLabel = this.dateService.formatDatePlace(order.details.datePlace, false, false, true);
        const currency = order.details.currency;
        const currencyTranslated = this.translateService.instant('currencies.symbol.'+currency);
        const consumerKey = order.consumer.key;
        const consumerName = order.consumer.key == 
          'anonymous'?(this.translateService.instant('consumer.AnonymousClient')) 
          :order.consumer.details.fullDetails.lastname + " " + order.consumer.details.fullDetails.firstname;
        const consumerPhone = order.consumer.details?.fullDetails?.phone || '';
        const consumerEmail = order.consumer.details?.fullDetails?.email || '';
        const key = order.key;
        const line = {
          orderId: key,
          datePlace: datePlaceLabel,
          client: consumerName,
          producer: null,
          status: this.translateService.instant("orderStatus.producer." + order.status),
          product: null,
          quantity: null,
          price: null,
          message: order.details.comment
        };
        const lineByClient = {
          client:{t:'s',v:consumerName,s:this.firstColStyle},
          datePlace:{t:'s',v:datePlaceLabel,s:this.firstColStyle},
        };

        byProductHeader[order.key] = {
          t:'s',v:consumerName,s:this.headerStyle
        }
        byProductHeader[order.key+'_price'] = {
          t:'s',v:(consumerName+'_price'),s:this.headerStyle
        }
        byProductSubHeader[order.key] = {
          t:'s',v:datePlaceLabel,s:this.headerStyle
        }

        if(!consumers[order.consumer.key]){
          consumers[order.consumer.key] = {
            client: {t:'s',v:(consumerName),s:{font:{bold:true}}},
            phone:consumerPhone,
            email:consumerEmail
          }
        }
        console.log(order);
        const producerName = order.producer?.details.shortDetails.name || order.tournee.producer.details.shortDetails.name;
        const producerKey = order.producer?.key || order.tournee.producer.key;        
      
        if(!producerLines[order.producer.key]){
          producerLines[order.producer.key] = {
            producer: producerName,
            nbr: 0,
            total: 0,
            deposit: 0,
            delivery: 0,
            currencyTranslated: currencyTranslated
          }
        }
        producerLines[order.producer.key].nbr +=1;
      
        for (const [productKey, product] of Object.entries(order.products.references)) {
          const _product: ProductModel = product;
          const quantity = _product.basket.volumeDelivered || _product.basket.quantityDelivered || _product.basket.quantity;
          const metric = _product.details.quantityMetric;
          const metricType = _product.details.quantityMetricType;
          const metricTranslated = this.translateService.instant('quantityMetric.short.'+metric);
          const priceMetric = _product.details.unitPriceQuantityMetric;
          const priceMetricType = _product.details.unitPriceQuantityMetricType;
          const sizeMetric = _product.details.sizeMetric;
          const priceMetricTranslated = this.translateService.instant('quantityMetric.short.'+priceMetric);
          const line2 = { ...line };
          line2.datePlace= datePlaceLabel;
          line2.producer = producerName;
          line2.product = _product.details.name;
          line2.quantity = quantity;
          const price = this.mathService.calculateProductTotalAmount(_product);
          const unitPrice = (_product.basket.unitPriceVATIncluded!==null && typeof _product.basket.unitPriceVATIncluded !=='undefined')?_product.basket.unitPriceVATIncluded:_product.details.unitPriceVATIncluded;

          line2.price = price;
          commandes.byLines.push(line2);
          producerLines[order.producer.key].total = this.mathService.sum(producerLines[order.producer.key].total,price);

          lineByClient[productKey] = {t:'n',v:quantity,z:('#0" '+metricTranslated+'"'),s:{border:{left:this.borderStyle}}};
          lineByClient[productKey+'_price'] = {t:'n',v:price,z:('"'+currencyTranslated+'" #0.00')};
          byClientHeader[productKey] = {t:'s',v:_product.details.name,s:this.headerStyle};
          byClientHeader[productKey+'_price'] = {t:'s',v:_product.details.name,s:this.headerStyle};
          if(!byProducts[producerKey]){
            byProducts[producerKey] = {};
          }
          if(!byProducts[producerKey][productKey]){
            byProducts[producerKey][productKey] = {
              productName:{t:'s',v:_product.details.name,s:this.firstColStyle},
            };
          }
          byProducts[producerKey][productKey][order.key] = {t:'n',v:quantity,z:('#0" '+metricTranslated+'"'),s:{border:{left:this.borderStyle}}};
          byProducts[producerKey][productKey][order.key+'_price'] = {t:'n',v:price,z:('"'+currencyTranslated+'" #0.00')};

          if(!products[productKey]){
            products[productKey] = {
              product: _product.details.name,
              quantity:quantity,
              price:price,
              currency:order.details.currency,
              metric: metric,
            }
          }

          if(!productStats[producerKey]){
              productStats[producerKey] = {
                name: producerName,
                products:{}
              }
          }
          if(!productStats[producerKey].products[productKey]){
            productStats[producerKey].products[productKey] = {
              name:_product.details.name,
              currency:order.details.currency,
              priceTranslated: currencyTranslated + unitPrice+'/'+priceMetricTranslated,
              price: unitPrice,
              priceMetricTranslated:priceMetricTranslated,
              metricTranslated: metricTranslated,
              currencyTranslated:currencyTranslated,
              metric: metric,
              orders:{},
              sizings:{}
            }
          }
          if(!productLines[productKey]){
            productLines[productKey] = {
              datePlace: datePlaceLabel,
              producer: producerName,
              product:_product.details.name,
              quantity:0,
              price:_product.details.unitPriceVATIncluded,
              total:0,
              metricTranslated:metricTranslated,
              currencyTranslated:currencyTranslated,
              priceMetricTranslated:priceMetricTranslated
            }
          }
          if(!productStats[producerKey].products[productKey].orders[consumerKey]){
              productStats[producerKey].products[productKey].orders[consumerKey] = {
                name:consumerName,
                lastname:order.consumer.details?.fullDetails?.lastname || '',
                firstname:order.consumer.details?.fullDetails?.firstname || '',
                quantity:0,
                price: 0,
                currencyTranslated: currencyTranslated,
              }
          }
          
          if(_product.key == 'deposit'){
            producerLines[order.producer.key].deposit = this.mathService.sum(producerLines[order.producer.key].deposit,price);
          }
          else if(_product.key == 'deliveryCost'){
            producerLines[order.producer.key].delivery = this.mathService.sum(producerLines[order.producer.key].delivery,price);
          }
          else {
            if(!productStats[producerKey].products[productKey].sizings[quantity]){
              productStats[producerKey].products[productKey].sizings[quantity] = {
                product: _product.details.name,
                datePlace: datePlaceLabel,
                priceTranslated: {t:'n',v:unitPrice,z:('"'+currencyTranslated+'" #0.00"/'+priceMetricTranslated+'"')},
                priceMetricTranslated: priceMetricTranslated,
                currencyTranslated:currencyTranslated,
                producer: producerName,
                sizing:quantity,
                metricTranslated:metricTranslated,
                quantity:0,
                total:0,
                price: 0,
              }
            }
            productStats[producerKey].products[productKey].sizings[quantity].quantity = this.mathService.sum(1,productStats[producerKey].products[productKey].sizings[quantity].quantity);
            productStats[producerKey].products[productKey].sizings[quantity].price = this.mathService.sum(price,productStats[producerKey].products[productKey].sizings[quantity].price);  
          }

          productLines[productKey].quantity = this.mathService.sum(quantity,productLines[productKey].quantity);
          const productToCalculate:ProductModel =  {
            details:{unitPriceVATIncluded:_product.details.unitPriceVATIncluded, quantityMetricType:metricType,
              unitPriceQuantityMetric:priceMetric, unitPriceQuantityMetricType:priceMetricType , sizeMetric:sizeMetric, quantityMetric:metric}, 
            basket:{quantity:productLines[productKey].quantity}};
          productLines[productKey].total = this.mathService.calculateProductTotalAmount(productToCalculate);
          // this.mathService.multiply(_product.details.unitPriceVATIncluded,productLines[productKey][this.quantityTranslation]);

          productStats[producerKey].products[productKey].orders[consumerKey].quantity = this.mathService.sum(quantity,productStats[producerKey].products[productKey].orders[consumerKey].quantity);
          productStats[producerKey].products[productKey].orders[consumerKey].price = this.mathService.sum(price,productStats[producerKey].products[productKey].orders[consumerKey].price);
        }

        byClients[key] = lineByClient;
      }
    }

    commandes.byLines.sort(function (a, b) {
      if (a.Client > b.Client) {
        return -1;
      } else if (a.Client < b.Client) {
        return 1;
      }
      return 0;
    });
    commandes.byLines.splice(0,0,lineHeader);

    console.log(productStats);
    for(const [producerKey,producer] of Object.entries(productStats)){

      // ADD a line with the producer name to separate the products
      if(Object.values(productStats).length>1){
        const producerSeparatorLine = 
          {productName:{t:'s',v:producer['name'],s:{font:{bold:true, color:{rgb:'003c3a'}}, border:{bottom:this.borderStyle}}}}
        commandes.byProduct.push(producerSeparatorLine);      
      }      
      
      for(const [productKey, product] of Object.entries(producer['products'])){
        for (const _order of orders){      // create empty cells to display continous borders
          if(!byClients[_order.key]){
            byClients[_order.key] = {};
          }
          if(!byClients[_order.key][productKey]){
            byClients[_order.key][productKey] = {t:'n',v:'',z:('#0" '+product['metricTranslated']+'"'), s:{border:{left:this.borderStyle}}};
            byClients[_order.key][productKey+'_price'] = {t:'n',v:'',z:('"'+product['currencyTranslated']+'" #0.00')};
          }
          if(!byProducts[producerKey][productKey][_order.key]){
            byProducts[producerKey][productKey][_order.key] = {t:'n',v:'',z:('#0" '+product['metricTranslated']+'"'), s:{border:{left:this.borderStyle}}};
            byProducts[producerKey][productKey][_order.key+'_price'] = {t:'n',v:'',z:('"'+product['currencyTranslated']+'" #0.00')};
          }
        }
        commandes.productSizings = commandes.productSizings.concat(Object.values(product['sizings']));
      }
      byProducts[producerKey].array = Object.values(byProducts[producerKey]).sort((a,b) => {
        return (a['productName'].v.toLowerCase() < b['productName'].v.toLowerCase())?-1:1;
      });
      commandes.byProduct = commandes.byProduct.concat(byProducts[producerKey].array);

      // ADD an empty line
      if(Object.values(productStats).length>1){
        commandes.byProduct.push({});      
      }   
    }

    commandes.productSizings = commandes.productSizings.map(item => {
      const newItem = {
          product:{t:'s',v:item.product,s:{font:{bold:true}}},
          datePlace:{t:'s',v:item.datePlace,z:('#0"x"'),s:{border:{left:this.borderStyle}}},
          sizing:{t:'n',v:item.sizing,z:('#0" '+item.metricTranslated+'"'),s:{border:{left:this.borderStyle}}},
          quantity:{t:'n',v:item.quantity,z:('#0"x"'),s:{border:{left:this.borderStyle}}},
          price:{...item.priceTranslated,s:{border:{left:this.borderStyle}}}
        }
      if(Object.values(productStats).length>1){
        newItem['producer'] = item.producer;
      }
      return newItem;
    })
    commandes.producerSummary = (Object.values(producerLines) as any)
    .sort((a,b) => {
      if(a.producer == b.producer) {
        return a.producer.toLowerCase() <b.producer.toLowerCase() ?1:-1
      }
      else if(a.producer.toLowerCase() <b.producer.toLowerCase()){
        return -1
      }
      else{
        return 1
      }
    })
    .map(item => {
      const newItem = {
        producer:{t:'s',v:item.producer,s:{font:{bold:true}}},
        nbr:{t:'n',v:item.nbr,z:('#0')},
        total:{t:'n',v:item.total,z:('"'+item.currencyTranslated+'" #0.00')},
      }
      return newItem;
    });

    commandes.productSummary = Object.values(productLines);
    commandes.productSummary = commandes.productSummary.map(item => {
      const newItem = {
        datePlace:{t:'s',v:item.datePlace,s:{font:{bold:true}}},
        producer:{t:'s',v:item.producer,s:{font:{bold:true}}},
        product:{t:'s',v:item.product,s:{font:{bold:true}}},
        price:{t:'n',v:item.price,z:('"'+item.currencyTranslated+'" #0.00"/'+item.priceMetricTranslated+'"'),s:{border:{left:this.borderStyle}}},
        quantity:{t:'n',v:item.quantity,z:('#0" '+item.metricTranslated+'"')},
        total:{t:'n',v:item.total,z:('"'+item.currencyTranslated+'" #0.00')},
      }
      if(Object.values(productStats).length>1){
        newItem[this.producerTranslation] = item[this.producerTranslation];
      }
      return newItem;
    })

    commandes.productSizings.splice(0,0,productSizingsHeader);

    commandes.byProduct.splice(0,0,byProductSubHeader);
    commandes.byProduct.splice(0,0,byProductHeader);
    
    commandes.byClient = Object.values(byClients).sort((a,b) => {
      if(a['client'] && b['client']){
        return (a['client'].v.toLowerCase() <b['client'].v.toLowerCase()) ? -1: 1;
      }
      else if(a['client']){
        return 1;
      }
      else if(b['client']){
        return -1;
      }
      else{
        return 0;
      }
    })
    commandes.byClient.splice(0,0,byClientHeader);

    commandes.consumers = Object.values(consumers).sort((a,b) => {
      return a['client'].v.toLowerCase()<b['client'].v.toLowerCase() ? -1: 1;
    })
    commandes.consumers.splice(0,0,clientHeader);

    commandes.products = Object.values(products);


    commandes.productSummary = commandes.productSummary.sort((a,b) => {
      if(a.producer.v == b.producer.v) {
        return a.product.v.toLowerCase() <b.product.v.toLowerCase() ?1:-1
      }
      else if(a.producer.v.toLowerCase() <b.producer.v.toLowerCase()){
        return -1
      }
      else{
        return 1
      }
    })
    commandes.productSummary.splice(0,0,productHeader);
    commandes.producerSummary.splice(0,0,producerSummaryHeader);
    console.log(commandes);
    this.exportAsExcelFile(commandes, filename);
  }


  public exportConsumers(items: ProfileModel[], filename: string) {
    const xls: any = {consumers:[],};
    const consumers = {};
    const clientHeader:any = {
      [this.clientTranslation]:{t:'s',v:this.clientTranslation,s:this.headerStyle},
      [this.phoneTranslation]:{t:'s',v:this.phoneTranslation,s:this.headerStyle},
      [this.emailTranslation]:{t:'s',v:this.emailTranslation,s:this.headerStyle},
    };

    for (const consumer of items) {
      const consumerName = consumer.key == 'anonymous'?(this.translateService.instant('consumer.AnonymousClient')) :consumer.details.fullDetails.lastname + " " + consumer.details.fullDetails.firstname;
      const consumerPhone = consumer.details?.fullDetails?.phone || '';
      const consumerEmail = consumer.details?.fullDetails?.email || ''
      if(consumer.details){
        consumers[consumer.key] ={
            [this.clientTranslation]: {t:'s',v:(consumerName),s:{font:{bold:true}}},
            [this.phoneTranslation]:consumerPhone,
            [this.emailTranslation]:consumerEmail
          }        
      }
    }
    xls.consumers = Object.values(consumers).sort((a,b) => {
      return a[this.clientTranslation].v.toLowerCase()<b[this.clientTranslation].v.toLowerCase() ? -1: 1;
    })
    xls.consumers.splice(0,0,clientHeader);
    console.log(consumers, xls);
    this.generateFileGeneric(xls, filename,"xlsx" as XLSX.BookType)
    // this.exportAsExcelFile(xls, filename);
  }
}
